import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { InPostPaczkomat } from '@/types';
import * as analytics from '@/lib/analytics';
import { InPostSelector } from '@/components/InPostSelector';
import { api } from '@/lib/api';
import { useCheckoutConfig } from '@/lib/hooks';

// Pricing response from backend
interface PricingResponse {
  subtotal: number;
  shipping: number;
  vat: number;
  total: number;
  netSubtotal: number;
  netShipping: number;
  freeShippingThreshold?: number;
  freeShippingApplied: boolean;
}
import {
  Container,
  Title,
  Grid,
  Paper,
  Text,
  TextInput,
  Button,
  Stack,
  Image,
  Alert,
  Divider,
  Group,
  ActionIcon,
  Loader,
  Badge,
  Radio,
  Checkbox,
  Box,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconPlus, IconMinus, IconAlertCircle } from '@tabler/icons-react';
import { useLanguage } from '@/context/LanguageContext';

const CheckoutForm: React.FC = () => {
  useNavigate(); // Keep for potential future use
  const { items, removeItem } = useCart();
  const { language, t } = useLanguage();
  const [selectedPaczkomat, setSelectedPaczkomat] = useState<InPostPaczkomat | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pricing, setPricing] = useState<PricingResponse | null>(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  
  // Load shipping config from backend (cached)
  const { data: shippingConfig } = useCheckoutConfig();

  // Separate in-stock and out-of-stock items (memoized to prevent infinite loops)
  const inStockItems = useMemo(() => items.filter(item => item.product.inStock), [items]);
  const outOfStockItems = useMemo(() => items.filter(item => !item.product.inStock), [items]);


  // Stable reference for items to use in pricing calculation
  const itemsForPricing = useMemo(() => 
    inStockItems.map(item => ({
      productId: item.product.id,
      price: item.product.price,
      quantity: item.quantity,
    })), 
    [inStockItems]
  );
  
  // Stringify for stable comparison in useEffect
  const itemsKey = useMemo(() => JSON.stringify(itemsForPricing), [itemsForPricing]);

  const form = useForm({
    initialValues: {
      // Contact info
      name: '',
      email: '',
      phone: '',
      // Shipping
      shippingMethod: '' as '' | 'inpost' | 'courier',
      // Shipping address (for courier)
      deliveryName: '',
      deliveryCompanyName: '',
      deliveryPhone: '',
      street: '',
      city: '',
      postCode: '',
      // VAT invoice
      needInvoice: false,
      companyName: '',
      taxId: '',
      invoiceAddress: '',
    },
    validate: {
      name: (value) => (value.length < 2 ? t('checkout.errors.name') || 'Name must be at least 2 characters' : null),
      email: (value) => (!/^\S+@\S+$/.test(value) ? t('checkout.errors.email') || 'Invalid email' : null),
      phone: (value) => (value.length < 9 ? t('checkout.errors.phone') || 'Phone number is too short' : null),
      shippingMethod: (value) => (!value ? t('checkout.errors.selectShippingMethod') || 'Please select shipping method' : null),
      street: (value, values) => {
        if (values.shippingMethod === 'courier' && !value) {
          return t('checkout.errors.fillAddress') || 'Please fill in street address';
        }
        return null;
      },
      city: (value, values) => {
        if (values.shippingMethod === 'courier' && !value) {
          return t('checkout.errors.fillAddress') || 'Please fill in city';
        }
        return null;
      },
      postCode: (value, values) => {
        if (values.shippingMethod === 'courier' && !value) {
          return t('checkout.errors.fillAddress') || 'Please fill in postal code';
        }
        return null;
      },
      deliveryName: (value, values) => {
        if (values.shippingMethod === 'courier' && !value) {
          return t('checkout.errors.deliveryName') || 'Please fill in recipient name';
        }
        return null;
      },
      deliveryPhone: (value, values) => {
        if (values.shippingMethod === 'courier' && (!value || value.length < 9)) {
          return t('checkout.errors.deliveryPhone') || 'Please fill in phone number';
        }
        return null;
      },
      companyName: (value, values) => {
        if (values.needInvoice && !value) {
          return t('checkout.errors.companyName') || 'Company name is required for VAT invoice';
        }
        return null;
      },
      taxId: (value, values) => {
        if (values.needInvoice && !value) {
          return t('checkout.errors.taxId') || 'Tax ID is required for VAT invoice';
        }
        return null;
      },
      invoiceAddress: (value, values) => {
        if (values.needInvoice && !value) {
          return t('checkout.errors.invoiceAddress') || 'Company address is required for VAT invoice';
        }
        return null;
      },
    },
  });

  // Calculate pricing from backend when shipping method or items change
  useEffect(() => {
    if (itemsForPricing.length === 0) {
      setPricing(null);
      return;
    }

    const shippingMethod = form.values.shippingMethod as 'inpost' | 'courier' | '';
    
    let cancelled = false;
    setPricingLoading(true);
    
    api.checkout.calculatePricing({
      items: itemsForPricing,
      shippingMethod: shippingMethod || undefined,
    })
      .then(response => {
        if (!cancelled) {
          setPricing(response);
        }
      })
      .catch(err => {
        console.error('Failed to calculate pricing:', err);
        if (!cancelled) {
          setPricing(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPricingLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [form.values.shippingMethod, itemsKey]); // Use itemsKey for stable comparison

  const handleSubmit = async (values: typeof form.values) => {
    if (inStockItems.length === 0) {
      setError(t('checkout.errors.onlyOutOfStock'));
      return;
    }

    // Validate all fields
    const validation = form.validate();
    if (validation.hasErrors) {
      setError(t('checkout.errors.validation') || 'Please fill in all required fields');
      return;
    }

    // Validate shipping method and paczkomat/address
    if (!values.shippingMethod) {
      setError(t('checkout.errors.selectShippingMethod'));
      return;
    }

    if (values.shippingMethod === 'inpost' && !selectedPaczkomat) {
      setError(t('checkout.errors.selectPaczkomat'));
      return;
    }

    if (values.shippingMethod === 'courier' && (!values.street || !values.city || !values.postCode || !values.deliveryName || !values.deliveryPhone)) {
      setError(t('checkout.errors.fillAddress'));
      return;
    }

    if (!pricing) {
      setError(t('checkout.errors.selectShippingMethod'));
      return;
    }

    const finalShippingMethod = values.shippingMethod as 'inpost' | 'courier';

    setProcessing(true);
    setError(null);

    // Track add shipping info (only for in-stock items)
    const gaItems = inStockItems.map(item => ({
      item_id: item.product.id,
      item_name: item.product.name,
      price: item.product.price,
      quantity: item.quantity,
    }));

    analytics.addShippingInfo(gaItems, pricing.subtotal, finalShippingMethod);
    analytics.addPaymentInfo(gaItems, pricing.total, 'stripe');

    try {
      // Prepare shipping info
      const shippingInfo: any = {
        method: finalShippingMethod,
        cost: pricing.shipping,
      };

      if (finalShippingMethod === 'inpost' && selectedPaczkomat) {
        shippingInfo.paczkomat = {
          name: selectedPaczkomat.name,
          locationId: selectedPaczkomat.location_id,
          address: selectedPaczkomat.address.line1,
          city: selectedPaczkomat.address.city,
          postCode: selectedPaczkomat.address.post_code,
        };
      } else if (finalShippingMethod === 'courier') {
        shippingInfo.address = {
          name: values.deliveryName,
          companyName: values.deliveryCompanyName || undefined,
          phone: values.deliveryPhone,
          street: values.street,
          city: values.city,
          postCode: values.postCode,
        };
      }

      const customer = {
        name: values.name,
        email: values.email,
        phone: values.phone,
      };

      const orderData: any = {
        items: inStockItems,
        subtotal: pricing.subtotal,
        shipping: shippingInfo,
        vat: pricing.vat,
        total: pricing.total,
        customer,
        language: language,
      };

      if (values.needInvoice) {
        orderData.vatInvoice = {
          companyName: values.companyName,
          taxId: values.taxId,
          address: values.invoiceAddress,
        };
      }

      // Get origin from window
      const origin = window.location.origin;

      // Call backend API to create Stripe Checkout Session
      const response = await api.checkout.createSession({
        amount: pricing.total,
        currency: 'pln',
        customerEmail: values.email,
        customerName: values.name,
        lineItems: inStockItems.map(item => ({
          name: item.product.name,
          amount: item.product.price * 100, // Convert to cents
          quantity: item.quantity,
        })),
        successUrl: `${origin}/order-confirmation/:id`, // Backend will replace :id with orderId
        cancelUrl: `${origin}/checkout`,
        items: inStockItems,
        shipping: shippingInfo,
        customer,
      });

      // Redirect to Stripe Checkout
      window.location.href = response.url;
    } catch (err: any) {
      setError(err.message || t('checkout.errors.submit') || 'Failed to create checkout session. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="xl">
        {/* Contact Information */}
        <Box>
          <Title order={3} mb="md">{t('checkout.contact.title')}</Title>
          <Stack gap="md">
            <TextInput
              label={t('checkout.contact.name')}
              placeholder={t('checkout.contact.namePlaceholder')}
              required
              {...form.getInputProps('name')}
            />
            <TextInput
              label={t('checkout.contact.email')}
              placeholder={t('checkout.contact.emailPlaceholder')}
              type="email"
              required
              {...form.getInputProps('email')}
            />
            <TextInput
              label={t('checkout.contact.phone')}
              placeholder={t('checkout.contact.phonePlaceholder')}
              type="tel"
              required
              {...form.getInputProps('phone')}
            />
          </Stack>
        </Box>

        <Divider />

        {/* Shipping Details */}
        <Box>
          <Title order={3} mb="md">{t('checkout.shipping.title')}</Title>
          <Stack gap="md">
            <Text fw={600} mb="xs">{t('checkout.shipping.method')}</Text>
            <Radio.Group
              value={form.values.shippingMethod}
              onChange={(value) => {
                form.setFieldValue('shippingMethod', value as '' | 'inpost' | 'courier');
                setError(null);
              }}
            >
              <Stack gap="xs">
                <Radio
                  value="inpost"
                  label={t('checkout.shipping.inpost')}
                  description={shippingConfig?.options?.inpost?.price ? `${shippingConfig.options.inpost.price.toFixed(2)} PLN` : ''}
                />
                {form.values.shippingMethod === 'inpost' && (
                  <Box style={{ marginLeft: '24px', marginTop: '8px' }}>
                    <InPostSelector
                      onSelect={setSelectedPaczkomat}
                      selectedPaczkomat={selectedPaczkomat || undefined}
                    />
                  </Box>
                )}
                <Radio
                  value="courier"
                  label={t('checkout.shipping.courier')}
                  description={shippingConfig?.options?.courier?.price ? `${shippingConfig.options.courier.price.toFixed(2)} PLN` : ''}
                />
                {form.values.shippingMethod === 'courier' && (
                  <Stack gap="md" style={{ marginLeft: '24px', marginTop: '8px' }}>
                    <TextInput
                      label={t('checkout.shipping.address.name')}
                      placeholder={t('checkout.shipping.address.namePlaceholder')}
                      required
                      {...form.getInputProps('deliveryName')}
                    />
                    <TextInput
                      label={t('checkout.shipping.address.companyName')}
                      placeholder={t('checkout.shipping.address.companyNamePlaceholder')}
                      {...form.getInputProps('deliveryCompanyName')}
                    />
                    <TextInput
                      label={t('checkout.shipping.address.phone')}
                      placeholder={t('checkout.shipping.address.phonePlaceholder')}
                      type="tel"
                      required
                      {...form.getInputProps('deliveryPhone')}
                    />
                    <TextInput
                      label={t('checkout.shipping.address.street')}
                      placeholder={t('checkout.shipping.address.streetPlaceholder')}
                      required
                      {...form.getInputProps('street')}
                    />
                    <TextInput
                      label={t('checkout.shipping.address.city')}
                      placeholder={t('checkout.shipping.address.cityPlaceholder')}
                      required
                      {...form.getInputProps('city')}
                    />
                    <TextInput
                      label={t('checkout.shipping.address.postCode')}
                      placeholder={t('checkout.shipping.address.postCodePlaceholder')}
                      required
                      {...form.getInputProps('postCode')}
                    />
                  </Stack>
                )}
              </Stack>
            </Radio.Group>
          </Stack>
        </Box>

        <Divider />

        {/* VAT Invoice */}
        <Box>
          <Title order={3} mb="md">{t('checkout.invoice.title')}</Title>
          <Stack gap="md">
            <Checkbox
              label={t('checkout.invoice.needInvoice')}
              {...form.getInputProps('needInvoice', { type: 'checkbox' })}
            />
            {form.values.needInvoice && (
              <Stack gap="md">
                <TextInput
                  label={t('checkout.invoice.companyName')}
                  placeholder={t('checkout.invoice.companyNamePlaceholder')}
                  required
                  {...form.getInputProps('companyName')}
                />
                <TextInput
                  label={t('checkout.invoice.taxId')}
                  placeholder={t('checkout.invoice.taxIdPlaceholder')}
                  required
                  {...form.getInputProps('taxId')}
                />
                <TextInput
                  label={t('checkout.invoice.address')}
                  placeholder={t('checkout.invoice.addressPlaceholder')}
                  required
                  {...form.getInputProps('invoiceAddress')}
                />
              </Stack>
            )}
          </Stack>
        </Box>

        <Divider />

        {/* Summary */}
        <Box>
          <Title order={3} mb="md">{t('checkout.summary.title')}</Title>
          {pricingLoading ? (
            <Group justify="center" py="md">
              <Loader size="sm" />
              <Text c="dimmed">{t('checkout.summary.calculating') || 'Obliczanie...'}</Text>
            </Group>
          ) : pricing ? (
            <Stack gap="xs">
              <Group justify="space-between">
                <Text>{t('checkout.summary.subtotal')}:</Text>
                <Text>{pricing.subtotal.toFixed(2)} {t('common.pln')}</Text>
              </Group>
              {pricing.freeShippingApplied ? (
                <Group justify="space-between">
                  <Text>{t('checkout.summary.shipping')}:</Text>
                  <Text c="green" fw={600}>{t('checkout.summary.freeShipping') || 'Darmowa dostawa!'}</Text>
                </Group>
              ) : (
                <Group justify="space-between">
                  <Text>{t('checkout.summary.shipping')}:</Text>
                  <Text>{pricing.shipping.toFixed(2)} {t('common.pln')}</Text>
                </Group>
              )}
              <Group justify="space-between">
                <Text c="dimmed" size="sm">{t('checkout.summary.vatIncluded') || 'w tym VAT (23%)'}:</Text>
                <Text c="dimmed" size="sm">{pricing.vat.toFixed(2)} {t('common.pln')}</Text>
              </Group>
              <Divider />
              <Group justify="space-between">
                <Text fw={700} size="lg">{t('checkout.summary.total')}:</Text>
                <Text fw={700} size="lg">{pricing.total.toFixed(2)} {t('common.pln')}</Text>
              </Group>
              {pricing.freeShippingThreshold && !pricing.freeShippingApplied && (
                <Text size="xs" c="dimmed" ta="center">
                  {t('checkout.summary.freeShippingNote', { threshold: pricing.freeShippingThreshold.toString() })}
                </Text>
              )}
            </Stack>
          ) : (
            <Text c="dimmed">{t('checkout.summary.selectShipping') || 'Wybierz metodę dostawy, aby zobaczyć sumę'}</Text>
          )}
        </Box>

        {outOfStockItems.length > 0 && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title={t('checkout.orderSummary.outOfStock')} mt="md">
            <Text size="sm" mb="xs">
              {t('checkout.errors.onlyOutOfStock')}
            </Text>
            <Stack gap="xs">
              {outOfStockItems.map(item => (
                <Group key={item.product.id} justify="space-between">
                  <Text size="sm">{item.product.name}</Text>
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    onClick={() => removeItem(item.product.id)}
                  >
                    {t('checkout.orderSummary.remove')}
                  </Button>
                </Group>
              ))}
            </Stack>
          </Alert>
        )}

        {error && (
          <Alert color="red" title="Error" mt="md">
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          size="lg"
          loading={processing}
          disabled={processing || inStockItems.length === 0 || !pricing}
        >
          {processing ? (
            <>
              <Loader size="sm" mr="xs" />
              {t('checkout.summary.redirecting')}
            </>
          ) : (
            t('checkout.summary.continueToPayment')
          )}
        </Button>
        <Text size="xs" c="dimmed" mt="xs" ta="center">
          {t('checkout.summary.securePaymentNote')}
        </Text>
      </Stack>
    </form>
  );
};

export default function CheckoutPage() {
  const { items, getTotal, updateQuantity } = useCart();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isChecking, setIsChecking] = useState(true);
  const [hasTrackedCheckout, setHasTrackedCheckout] = useState(false);

  // Wait for cart to hydrate from localStorage before checking
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsChecking(false);
      if (items.length === 0) {
        navigate('/');
      } else if (!hasTrackedCheckout) {
        // Track begin checkout
        const gaItems = items.map(item => ({
          item_id: item.product.id,
          item_name: item.product.name,
          price: item.product.price,
          quantity: item.quantity,
        }));

        analytics.beginCheckout(gaItems, getTotal());
        setHasTrackedCheckout(true);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [items, navigate, hasTrackedCheckout, getTotal]);

  if (isChecking || items.length === 0) {
    return null;
  }

  const subtotal = items.filter(item => item.product.inStock).reduce((sum, item) => sum + item.product.price * item.quantity, 0);

  return (
    <Container size="xl" py="xl">
      <Title order={1} mb="xl">{t('checkout.title')}</Title>

      <Grid>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Paper p="md" withBorder>
            <Title order={3} mb="md">{t('checkout.orderSummary.title')}</Title>
            <Stack gap="md">
              {items.map(item => (
                <Group key={item.product.id} gap="sm" align="center" wrap="nowrap">
                  <div style={{ width: '50px', height: '50px', flexShrink: 0, overflow: 'hidden', borderRadius: '4px', position: 'relative' }}>
                    <Image
                      src={item.product.images && item.product.images.length > 0 ? item.product.images[0] : undefined}
                      alt={item.product.name}
                      w={50}
                      h={50}
                      fit="cover"
                      radius="sm"
                      fallbackSrc="https://via.placeholder.com/50"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: item.product.inStock ? 1 : 0.5 }}
                    />
                    {!item.product.inStock && (
                      <Badge
                        color="red"
                        size="xs"
                        variant="filled"
                        style={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                        }}
                      >
                        {t('checkout.orderSummary.outOfStock')}
                      </Badge>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Group gap="xs" align="center" mb={4}>
                      <Text fw={600} size="sm" lineClamp={2} style={{ flex: 1 }}>{item.product.name}</Text>
                      {!item.product.inStock && (
                        <Badge color="red" size="xs" variant="light">{t('checkout.orderSummary.outOfStock')}</Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed" mb="xs">
                      {item.product.price.toFixed(2)} {t('common.pln')} each
                    </Text>
                    <Group gap="xs" align="center">
                      <ActionIcon
                        variant="light"
                        size="sm"
                        onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                        disabled={!item.product.inStock}
                      >
                        <IconMinus size={14} />
                      </ActionIcon>
                      <Text size="sm" fw={600} style={{ minWidth: '30px', textAlign: 'center' }}>
                        {item.quantity}
                      </Text>
                      <ActionIcon
                        variant="light"
                        size="sm"
                        onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                        disabled={!item.product.inStock}
                      >
                        <IconPlus size={14} />
                      </ActionIcon>
                      <Text size="sm" fw={600} ml="auto">
                        {(item.product.price * item.quantity).toFixed(2)} {t('common.pln')}
                      </Text>
                    </Group>
                  </div>
                </Group>
              ))}
              <Divider />
              <Group justify="space-between">
                <Text size="lg" fw={700}>
                  {t('checkout.summary.subtotal')}: {subtotal.toFixed(2)} {t('common.pln')}
                </Text>
              </Group>
            </Stack>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 7 }}>
          <Paper p="md" withBorder>
            <CheckoutForm />
          </Paper>
        </Grid.Col>
      </Grid>
    </Container>
  );
}

